import axios from 'axios'
import { tour, room } from '@/config/api'

// Create axios instance for tour worker
const tourClient = axios.create({
  baseURL: tour,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Create axios instance for room worker
const roomClient = axios.create({
  baseURL: room,
  headers: {
    'Content-Type': 'application/json'
  }
})

/**
 * Search for travel data using the tour worker
 * @param {string} query - Search query
 * @param {string} type - Type filter (optional)
 * @param {string} state - State filter (optional)
 * @returns {Promise<Object>} - Search results
 */
export async function searchTravelData(query, type, state) {
  try {
    // If type is provided but no query, directly search by type/category
    if (type && (!query || query.trim() === '')) {
      return searchByType(query, type, state);
    }

    // Clean up the query to make it URL-friendly
    const cleanQuery = encodeURIComponent(query.trim());

    if (!cleanQuery) {
      return { results: [] };
    }

    // Direct search by name using the /:name endpoint
    const response = await tourClient.get(`/${cleanQuery}`);

    if (response.data && response.data.success && response.data.data) {
      // Format the response to match the expected format in the ExploreView component
      return {
        results: [{
          id: response.data.data.id,
          name: response.data.data.name,
          description: response.data.data.description,
          image: response.data.data.images && response.data.data.images.length > 0 ?
                 response.data.data.images[0] : null,
          images: response.data.data.images || [],
          type: response.data.data.category,
          state: response.data.data.state,
          city: response.data.data.city,
          location: `${response.data.data.city}, ${response.data.data.state}`,
          latitude: response.data.data.latitude,
          longitude: response.data.data.longitude,
          score: 1.0,
          things_you_ll_love: response.data.data.things_you_ll_love,
          peak_season: response.data.data.peak_season,
          offseason: response.data.data.offseason,
          what_to_expect: response.data.data.what_to_expect,
          fullData: response.data.data
        }]
      };
    }

    return { results: [] };
  } catch (error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      // If we get a 404 (not found), try searching by type or category
      if (error.response.status === 404 && type) {
        return searchByType(query, type, state);
      }
    } else if (error.request) {
      // The request was made but no response was received
    }

    return { results: [] };
  }
}

/**
 * Search for travel data by type or category
 * @param {string} query - Search query (unused in this function but kept for consistency)
 * @param {string} type - Type filter
 * @param {string} state - State filter (optional)
 * @returns {Promise<Object>} - Search results
 */
export async function searchByType(query, type, state) {
  try {
    const params = {};

    // Determine if we're searching by type or category based on the category name
    const lowerType = type.toLowerCase();

    // These are all categories, not types
    const categoryValues = [
      'beach',
      'wildlife',
      'city'
    ];

    // Check if the type is actually a category
    if (categoryValues.includes(lowerType)) {
      params.category = lowerType;
    } else {
      params.type = lowerType;
    }

    const response = await tourClient.get('/find', { params });

    if (response.data && response.data.success && response.data.data) {
      const results = [];

      // Handle destinations (type search)
      if (response.data.data.destinations) {
        response.data.data.destinations.forEach(destination => {
          results.push({
            id: destination.id,
            name: destination.name,
            description: destination.description,
            image: destination.images && destination.images.length > 0 ?
                   destination.images[0] : null,
            images: destination.images || [],
            type: destination.category,
            state: destination.state,
            city: destination.city,
            location: `${destination.city}, ${destination.state}`,
            latitude: destination.latitude,
            longitude: destination.longitude,
            score: 1.0,
            things_you_ll_love: destination.things_you_ll_love,
            peak_season: destination.peak_season,
            offseason: destination.offseason,
            what_to_expect: destination.what_to_expect,
            fullData: destination
          });
        });
      }

      // Handle cities (category search)
      if (response.data.data.cities) {
        response.data.data.cities.forEach(city => {
          results.push({
            id: city.id || `city-${city.name.toLowerCase().replace(/\s+/g, '-')}`,
            name: city.name,
            description: city.description,
            image: city.images && city.images.length > 0 ?
                   city.images[0] : null,
            images: city.images || [],
            type: city.category,
            state: city.state,
            city: city.name,
            location: `${city.name}, ${city.state}`,
            latitude: city.latitude,
            longitude: city.longitude,
            score: 1.0,
            things_you_ll_love: city.things_you_ll_love,
            peak_season: city.peak_season,
            offseason: city.offseason,
            what_to_expect: city.what_to_expect,
            fullData: city
          });
        });
      }

      // Filter by state if provided
      if (state && state.trim()) {
        const stateFilter = state.trim().toLowerCase();
        return {
          results: results.filter(item =>
            item.state && item.state.toLowerCase().includes(stateFilter)
          )
        };
      }

      return { results };
    }

    return { results: [] };
  } catch (error) {
    return { results: [] };
  }
}

/**
 * Get health status of the tour worker
 * @returns {Promise<Object>} - Health status
 */
export async function getTourWorkerHealth() {
  try {
    const response = await tourClient.get('/health');
    return response.data;
  } catch (error) {
    return { status: 'error', message: error.message };
  }
}

/**
 * Search for restaurants near a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} - Search results
 */
export async function searchRestaurants(lat, lon) {
  try {
    const response = await roomClient.get('/restaurants', {
      params: { lat, lon }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch restaurants');
  }
}

/**
 * Search for shopping places near a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} - Search results
 */
export async function searchShopping(lat, lon) {
  try {
    const response = await roomClient.get('/shopping', {
      params: { lat, lon }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch shopping places');
  }
}

/**
 * Search for bus stops and public transport near a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} - Search results
 */
export async function searchBuses(lat, lon) {
  try {
    const response = await roomClient.get('/buses', {
      params: { lat, lon }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch bus stops');
  }
}
